import {
  getDictationRefusal,
  type DocumentAnswers,
  type FinalDocumentRevision,
  type LiveTranscriptionSession,
  type TranscriptChunk,
  type TranscriptorApiClient,
} from '@/shared/api/transcriptor';

import {
  LIVE_AUDIO_QUEUE_LIMIT,
  LIVE_FINAL_ANSWERS_READ_TIMEOUT_MS,
  LIVE_FINAL_FILL_TIMEOUT_MS,
  LIVE_TRANSCRIPT_FINISH_TIMEOUT_MS,
  RECORDING_MODE_LIVE_MODE,
  type TreatmentProcessingMode,
} from '../config';
import { LIVE_PCM_SAMPLE_RATE, measurePcm16Level } from '../services';

import {
  createAnswerEdits,
  type AnswerEdit,
  type EditedDocument,
} from './answerEdits';
import { createFillScheduler } from './liveFill';
import {
  abandonLiveSegments,
  applyLiveTranscriptionEvent,
  commitLiveSegment,
  EMPTY_LIVE_TRANSCRIPT,
  getLivePartialText,
  hasPendingLiveSegments,
  takeLiveChunks,
  type LiveTranscriptionEvent,
} from './liveTranscript';
import { isSupportedLiveSession } from './liveTranscriptionProvider';
import {
  advanceTurn,
  finishTurn,
  startTurn,
  type TurnDecision,
} from './liveTurns';
import type {
  RealtimeTranscriptionConnection,
  RealtimeTranscriptionHandlers,
} from './realtimeTranscription';

export type LiveDictationStatus =
  | 'starting'
  | 'live'
  | 'paused'
  | 'reconnecting'
  | 'finishing'
  | 'finished'
  | 'unavailable'
  | 'interrupted';

// `off`: the clinician records without live mode; manual answers are still
// saved. `server`: the server refused the live session, `provider`: the
// transcription service could not be reached, `dictationLimit`: the server
// refused the dictation's audio or fill after its time limit.
export type LiveUnavailableReason =
  | 'off'
  | 'unsupported'
  | 'microphone'
  | 'server'
  | 'provider'
  | 'dictationLimit';

export type LiveDocumentState = EditedDocument & {
  fillFailed: boolean;
  // The answers stored after processing could not be read.
  finalFailed: boolean;
};

export type LiveDictationState = {
  encounterId: string;
  mode: TreatmentProcessingMode;
  status: LiveDictationStatus;
  reason: LiveUnavailableReason | null;
  providerError: boolean;
  chunks: TranscriptChunk[];
  partial: string;
  documents: Record<string, LiveDocumentState>;
};

// A dictation is recognised and filled while it is spoken; after Stop scribe
// finishes its note from the live transcript, with no file to recognise.
export const isDictation = (live: LiveDictationState | null | undefined) =>
  live?.mode === RECORDING_MODE_LIVE_MODE.dictation;

export type LiveAudioCapture = {
  start: (
    stream: MediaStream,
    onSamples: (samples: Int16Array) => void,
  ) => Promise<void>;
  flush?: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
};

export type LiveDictationDeps = {
  api: Pick<
    TranscriptorApiClient,
    | 'createLiveTranscriptionSession'
    | 'fillProtocol'
    | 'getDocumentAnswers'
    | 'updateDocumentAnswers'
  >;
  // Null when the browser cannot capture PCM (no AudioWorklet).
  createCapture: () => LiveAudioCapture | null;
  connect: (
    session: LiveTranscriptionSession,
    handlers: RealtimeTranscriptionHandlers,
  ) => RealtimeTranscriptionConnection;
};

export type LiveDictation = {
  encounterId: string;
  start: (stream: MediaStream | null) => Promise<void>;
  setDocuments: (documentIds: string[]) => void;
  pause: () => void;
  resume: () => void;
  // The final revisions, or nothing when the whole dictation did not reach
  // every protocol: scribe then completes the note from what it stored.
  stop: () => Promise<FinalDocumentRevision[] | undefined>;
  edit: (documentId: string, edit: AnswerEdit) => void;
  // A snapshot stored by another request; older revisions are ignored.
  receiveAnswers: (documentId: string, answers: DocumentAnswers) => void;
  // Reads the answers processing finished the document with.
  loadFinalAnswers: (documentId: string) => Promise<void>;
  // Reads the stored answers, e.g. the view of a block note before any fill.
  readAnswers: (documentId: string) => Promise<void>;
  dispose: () => void;
};

type AudioOperation =
  | { type: 'append'; samples: Int16Array }
  | { type: 'commit' }
  | { type: 'clear' };

const EMPTY_DOCUMENT: LiveDocumentState = {
  snapshot: null,
  pending: {},
  fillFailed: false,
  saveFailed: false,
  finalFailed: false,
};

const ACTIVE_STATUSES: LiveDictationStatus[] = [
  'starting',
  'live',
  'paused',
  'reconnecting',
];

export const createLiveDictation = ({
  encounterId,
  language,
  mode = 'liveFill',
  deps,
  onChange,
  onSaved,
}: {
  encounterId: string;
  language: string;
  mode?: TreatmentProcessingMode;
  deps: LiveDictationDeps;
  onChange: (state: LiveDictationState) => void;
  // A manual edit was stored.
  onSaved?: (documentId: string) => void;
}): LiveDictation => {
  const runtime = {
    state: {
      encounterId,
      mode,
      status: 'starting',
      reason: null,
      providerError: false,
      chunks: [],
      partial: '',
      documents: {},
    } as LiveDictationState,
    transcript: EMPTY_LIVE_TRANSCRIPT,
    turn: startTurn(0),
    clockSamples: 0,
    capture: null as LiveAudioCapture | null,
    connection: null as RealtimeTranscriptionConnection | null,
    offline: [] as AudioOperation[],
    everOpened: false,
    reconnected: false,
    paused: false,
    stopped: false,
    stopping: null as Promise<FinalDocumentRevision[] | undefined> | null,
    transcriptLost: false,
    disposed: false,
    finishWaiters: [] as (() => void)[],
    documentIds: [] as string[],
  };

  const emit = (recipe: (state: LiveDictationState) => LiveDictationState) => {
    if (runtime.disposed) {
      return;
    }
    runtime.state = recipe(runtime.state);
    onChange(runtime.state);
  };

  const setStatus = (
    status: LiveDictationStatus,
    reason: LiveUnavailableReason | null = null,
  ) => emit((state) => ({ ...state, status, reason }));

  const updateDocument = (
    documentId: string,
    recipe: (document: LiveDocumentState) => LiveDocumentState,
  ) =>
    emit((state) => ({
      ...state,
      documents: {
        ...state.documents,
        [documentId]: recipe(state.documents[documentId] ?? EMPTY_DOCUMENT),
      },
    }));

  const edits = createAnswerEdits({
    encounterId,
    api: deps.api,
    readRevision: (documentId) =>
      runtime.state.documents[documentId]?.snapshot?.revision ?? 0,
    updateDocument: (documentId, recipe) =>
      updateDocument(documentId, (document) => ({
        ...document,
        ...recipe(document),
      })),
    onSaved,
  });
  const { applySnapshot } = edits;

  const scheduler = createFillScheduler<DocumentAnswers | null>({
    send: async (documentId, revision) => {
      const timeout = { timer: 0 };
      try {
        const answers = await Promise.race([
          deps.api.fillProtocol({
            encounterId,
            documentId,
            transcriptRevision: revision,
            language,
            chunks: runtime.state.chunks.slice(0, revision),
          }),
          new Promise<never>((_, reject) => {
            timeout.timer = window.setTimeout(
              () => reject(new Error('The final live fill timed out')),
              LIVE_FINAL_FILL_TIMEOUT_MS,
            );
          }),
        ]);
        if (answers?.appliedTranscriptRevision !== revision) {
          throw new Error('The final transcript revision was not applied');
        }
        return answers;
      } catch (error) {
        const recovery = { timer: 0 };
        try {
          const stored = await Promise.race([
            deps.api
              .getDocumentAnswers({ encounterId, documentId })
              .catch(() => null),
            new Promise<null>((resolve) => {
              recovery.timer = window.setTimeout(
                () => resolve(null),
                LIVE_FINAL_ANSWERS_READ_TIMEOUT_MS,
              );
            }),
          ]);
          if (stored?.appliedTranscriptRevision === revision) {
            return stored;
          }
          throw error;
        } finally {
          window.clearTimeout(recovery.timer);
        }
      } finally {
        window.clearTimeout(timeout.timer);
      }
    },
    onResult: (documentId, answers) => {
      applySnapshot(documentId, answers);
      updateDocument(documentId, (document) =>
        document.fillFailed ? { ...document, fillFailed: false } : document,
      );
    },
    onError: (documentId, error) => {
      if (getDictationRefusal(error) === 'dictation_limit_reached') {
        endAtDictationLimit();
        return;
      }
      updateDocument(documentId, (document) => ({
        ...document,
        fillFailed: true,
      }));
    },
  });

  // A failed fill is already reported on its document.
  const finishFills = async () => {
    if (runtime.disposed) {
      scheduler.dispose();
      return false;
    }
    return scheduler
      .finish(runtime.documentIds, runtime.state.chunks.length)
      .then(
        () => !runtime.disposed,
        () => false,
      )
      .finally(() => scheduler.dispose());
  };

  // The transcript revision is the number of completed phrases.
  const requestFills = (documentIds: string[]) => {
    const revision = runtime.state.chunks.length;
    if (revision && !runtime.stopped) {
      documentIds.forEach((documentId) =>
        scheduler.request(documentId, revision),
      );
    }
  };

  const perform = (operation: AudioOperation) => {
    const { connection } = runtime;
    if (!connection) {
      if (runtime.offline.length >= LIVE_AUDIO_QUEUE_LIMIT) {
        runtime.transcriptLost = true;
      }
      runtime.offline = [...runtime.offline, operation].slice(
        -LIVE_AUDIO_QUEUE_LIMIT,
      );
      return;
    }
    if (operation.type === 'append') {
      connection.appendAudio(operation.samples);
      return;
    }
    if (operation.type === 'commit') {
      connection.commit();
      return;
    }
    connection.clear();
  };

  const clockSeconds = () => runtime.clockSamples / LIVE_PCM_SAMPLE_RATE;

  const endTurn = (decision: TurnDecision) => {
    const { turn } = runtime;
    if (decision === 'commit') {
      perform({ type: 'commit' });
      runtime.transcript = commitLiveSegment(runtime.transcript, {
        start: turn.start,
        end: turn.start + turn.duration,
      });
    }
    if (decision === 'discard' && turn.duration > 0) {
      perform({ type: 'clear' });
    }
    runtime.turn = startTurn(clockSeconds());
  };

  const onSamples = (samples: Int16Array) => {
    if (runtime.stopped || runtime.disposed || runtime.paused) {
      return;
    }
    runtime.clockSamples += samples.length;
    perform({ type: 'append', samples });
    const { turn, decision } = advanceTurn({
      turn: runtime.turn,
      seconds: samples.length / LIVE_PCM_SAMPLE_RATE,
      level: measurePcm16Level(samples),
    });
    runtime.turn = turn;
    if (decision !== 'continue') {
      endTurn(decision);
    }
  };

  const resolveFinish = () => {
    if (!hasPendingLiveSegments(runtime.transcript)) {
      runtime.finishWaiters.forEach((finish) => finish());
    }
  };

  const waitForFinish = () =>
    new Promise<void>((resolve, reject) => {
      const finish = () => {
        window.clearTimeout(timer);
        runtime.finishWaiters = runtime.finishWaiters.filter(
          (waiter) => waiter !== finish,
        );
        resolve();
      };
      const timer = window.setTimeout(() => {
        runtime.finishWaiters = runtime.finishWaiters.filter(
          (waiter) => waiter !== finish,
        );
        reject(new Error('The transcription provider did not finish'));
      }, LIVE_TRANSCRIPT_FINISH_TIMEOUT_MS);
      runtime.finishWaiters = [...runtime.finishWaiters, finish];
      resolveFinish();
    });

  const onTranscriptionEvent = (event: LiveTranscriptionEvent) => {
    if (event.type === 'failed') {
      runtime.transcriptLost = true;
    }
    const { state: transcript, chunks } = takeLiveChunks(
      applyLiveTranscriptionEvent(runtime.transcript, event),
    );
    runtime.transcript = transcript;
    emit((state) => ({
      ...state,
      partial: getLivePartialText(transcript),
      chunks: chunks.length ? [...state.chunks, ...chunks] : state.chunks,
    }));
    if (chunks.length) {
      requestFills(runtime.documentIds);
    }
    resolveFinish();
  };

  const stopCapture = () => {
    runtime.capture?.stop();
    runtime.capture = null;
  };

  // The live part is over once scribe refuses the dictation past its limit;
  // the recording itself is stopped by its owner.
  const endAtDictationLimit = () => {
    stopCapture();
    runtime.connection?.close();
    runtime.connection = null;
    runtime.offline = [];
    setStatus('unavailable', 'dictationLimit');
  };

  // A provider that never accepted the connection is reported as such, not
  // as a dropped connection.
  const interrupt = () => {
    stopCapture();
    runtime.offline = [];
    if (runtime.everOpened) {
      setStatus('interrupted');
    } else {
      setStatus('unavailable', 'provider');
    }
  };

  // `ended`: Stop or dispose came while the session was minted, so no socket
  // is opened that nothing would close.
  const openConnection = async (): Promise<
    'opened' | 'ended' | LiveUnavailableReason
  > => {
    try {
      const session =
        await deps.api.createLiveTranscriptionSession(encounterId);
      if (runtime.stopped || runtime.disposed) {
        return 'ended';
      }
      if (!isSupportedLiveSession(session)) {
        return 'server';
      }
      const connection: RealtimeTranscriptionConnection = deps.connect(
        session,
        {
          onOpen: () => {
            if (runtime.connection === connection && !runtime.stopped) {
              runtime.everOpened = true;
              setStatus(runtime.paused ? 'paused' : 'live');
            }
          },
          onEvent: (event) => {
            if (runtime.connection === connection) {
              onTranscriptionEvent(event);
            }
          },
          onProviderError: () => {
            runtime.transcriptLost = true;
            emit((state) => ({ ...state, providerError: true }));
          },
          onClose: () => {
            if (runtime.connection === connection) {
              void onConnectionLost();
            }
          },
        },
      );
      runtime.connection = connection;
      const operations = runtime.offline;
      runtime.offline = [];
      operations.forEach(perform);
      return 'opened';
    } catch (error) {
      return getDictationRefusal(error) === 'dictation_limit_reached'
        ? 'dictationLimit'
        : 'server';
    }
  };

  // True when every phrase of the dictation was transcribed.
  const finishTranscript = async () => {
    const { capture } = runtime;
    if (!capture || !ACTIVE_STATUSES.includes(runtime.state.status)) {
      return false;
    }
    const flushed = capture.flush
      ? await capture.flush().then(
          () => true,
          () => false,
        )
      : true;
    endTurn(finishTurn(runtime.turn));
    runtime.stopped = true;
    stopCapture();
    setStatus('finishing');
    const settled = await waitForFinish().then(
      () => true,
      () => false,
    );
    return (
      flushed &&
      settled &&
      !runtime.disposed &&
      !runtime.transcriptLost &&
      !runtime.offline.length &&
      runtime.everOpened
    );
  };

  // Every phrase transcribed goes to scribe before Stop, also when the live
  // part ended early, because scribe completes the note from what it stored.
  const finish = async (): Promise<FinalDocumentRevision[] | undefined> => {
    if (mode === 'upload') {
      runtime.stopped = true;
      scheduler.dispose();
      return undefined;
    }
    const transcribed = await finishTranscript();
    runtime.stopped = true;
    stopCapture();
    runtime.connection?.close();
    runtime.connection = null;
    runtime.offline = [];
    const filled = await finishFills();
    const complete = transcribed && filled;
    if (runtime.state.status !== 'unavailable') {
      setStatus(complete ? 'finished' : 'interrupted');
    }
    return complete
      ? runtime.documentIds.map((documentId) => ({
          document_id: documentId,
          transcript_revision: runtime.state.chunks.length,
        }))
      : undefined;
  };

  const onConnectionLost = async () => {
    runtime.transcriptLost ||=
      hasPendingLiveSegments(runtime.transcript) || runtime.turn.duration > 0;
    runtime.connection = null;
    runtime.transcript = abandonLiveSegments(runtime.transcript);
    runtime.turn = startTurn(clockSeconds());
    emit((state) => ({ ...state, partial: '' }));
    resolveFinish();
    if (runtime.stopped || runtime.disposed) {
      return;
    }
    if (runtime.reconnected) {
      interrupt();
      return;
    }
    runtime.reconnected = true;
    setStatus('reconnecting');
    const result = await openConnection();
    if (result !== 'opened' && result !== 'ended') {
      interrupt();
    }
  };

  return {
    encounterId,

    start: async (stream) => {
      emit((state) => state);
      if (mode === 'upload') {
        setStatus('unavailable', 'off');
        return;
      }
      const capture = deps.createCapture();
      if (!capture) {
        setStatus('unavailable', 'unsupported');
        return;
      }
      if (!stream) {
        setStatus('unavailable', 'microphone');
        return;
      }
      runtime.capture = capture;
      try {
        await capture.start(stream, onSamples);
      } catch {
        stopCapture();
        setStatus('unavailable', 'microphone');
        return;
      }
      const result = await openConnection();
      if (result !== 'opened' && result !== 'ended') {
        stopCapture();
        runtime.offline = [];
        setStatus('unavailable', result);
      }
    },

    // Additive: an encounter preview without documents must not stop fills.
    setDocuments: (documentIds) => {
      const added = documentIds.filter(
        (documentId) => !runtime.documentIds.includes(documentId),
      );
      runtime.documentIds = [...runtime.documentIds, ...added];
      if (!added.length) {
        return;
      }
      emit((state) => ({
        ...state,
        documents: {
          ...Object.fromEntries(
            added.map((documentId) => [documentId, EMPTY_DOCUMENT]),
          ),
          ...state.documents,
        },
      }));
      requestFills(added);
    },

    pause: () => {
      if (!runtime.capture || runtime.paused) {
        return;
      }
      runtime.paused = true;
      endTurn(finishTurn(runtime.turn));
      runtime.capture.pause();
      if (runtime.state.status === 'live') {
        setStatus('paused');
      }
    },

    resume: () => {
      if (!runtime.capture || !runtime.paused) {
        return;
      }
      runtime.paused = false;
      runtime.turn = startTurn(clockSeconds());
      runtime.capture.resume();
      if (runtime.state.status === 'paused') {
        setStatus('live');
      }
    },

    // Every caller waits for the same finish, the Stop request among them:
    // it goes out once the last fill has.
    stop: () => {
      runtime.stopping = runtime.stopping ?? finish();
      return runtime.stopping;
    },

    edit: edits.edit,

    receiveAnswers: applySnapshot,

    loadFinalAnswers: async (documentId) => {
      try {
        applySnapshot(
          documentId,
          await deps.api.getDocumentAnswers({ encounterId, documentId }),
        );
      } catch {
        updateDocument(documentId, (document) => ({
          ...document,
          finalFailed: true,
        }));
      }
    },

    readAnswers: async (documentId) => {
      await deps.api
        .getDocumentAnswers({ encounterId, documentId })
        .then((answers) => applySnapshot(documentId, answers))
        .catch(() => {
          /* ignore — the next fill brings the answers too */
        });
    },

    dispose: () => {
      runtime.disposed = true;
      stopCapture();
      runtime.connection?.close();
      runtime.connection = null;
      runtime.offline = [];
      scheduler.dispose();
      edits.dispose();
      runtime.finishWaiters.forEach((finish) => finish());
    },
  };
};
